import React from 'react';
import styled from 'styled-components';

interface MarkerPreviewProps {
  styleType: string;
  markerColor: string;
}

export const MarkerPreview: React.FC<MarkerPreviewProps> = ({ styleType, markerColor }) => {
  // Create a 7x7 pattern for the corner marker (standard QR corner size)
  const pattern = [
    [1, 1, 1, 1, 1, 1, 1],
    [1, 0, 0, 0, 0, 0, 1],
    [1, 0, 1, 1, 1, 0, 1],
    [1, 0, 1, 1, 1, 0, 1],
    [1, 0, 1, 1, 1, 0, 1],
    [1, 0, 0, 0, 0, 0, 1],
    [1, 1, 1, 1, 1, 1, 1],
  ];

  const generateMarkerPath = (x: number, y: number, size: number) => {
    const type = styleType.replace('marker-', '').trim().toLowerCase();
    
    switch (type) {
      case 'dot':
        const centerX = x + size / 2;
        const centerY = y + size / 2;
        return `M${centerX},${centerY} m-${size / 2},0 a${size / 2},${size / 2} 0 1,0 ${size},0 a${size / 2},${size / 2} 0 1,0 -${size},0`;
      case 'square':
        return `M${x},${y} h${size} v${size} h-${size}z`;
      case 'rounded':
        const cornerRadius = size * 0.25;
        return `M${x + cornerRadius},${y} h${size - 2 * cornerRadius} q${cornerRadius},0 ${cornerRadius},${cornerRadius} v${size - 2 * cornerRadius} q0,${cornerRadius} -${cornerRadius},${cornerRadius} h-${size - 2 * cornerRadius} q-${cornerRadius},0 -${cornerRadius},-${cornerRadius} v-${size - 2 * cornerRadius} q0,-${cornerRadius} ${cornerRadius},-${cornerRadius}`;
      case 'diamond':
        const mid = size / 2;
        return `M${x + mid},${y} L${x + size},${y + mid} L${x + mid},${y + size} L${x},${y + mid}z`;
      case 'hexagon':
        const hexH = size * Math.sin(Math.PI / 3);
        const hexCx = x + size / 2;
        const hexCy = y + size / 2;
        return `M${hexCx},${hexCy - size/2} L${hexCx + hexH/2},${hexCy - size/4} L${hexCx + hexH/2},${hexCy + size/4} L${hexCx},${hexCy + size/2} L${hexCx - hexH/2},${hexCy + size/4} L${hexCx - hexH/2},${hexCy - size/4}Z`;
      case 'star':
        const cx2 = x + size / 2;
        const cy2 = y + size / 2;
        const starOuterRadius = size / 2;
        const starInnerRadius = size / 4;
        let path = `M ${cx2},${cy2 - starOuterRadius} `;
        for (let i = 0; i < 10; i++) {
          const radius = i % 2 === 0 ? starOuterRadius : starInnerRadius;
          const angle = (Math.PI / 5) * i;
          const pointX = cx2 + radius * Math.sin(angle);
          const pointY = cy2 - radius * Math.cos(angle);
          path += `L ${pointX},${pointY} `;
        }
        return path + 'Z';
      case 'cross':
        const crossWidth = size * 0.3;
        const crossStart = (size - crossWidth) / 2;
        return `
          M${x + crossStart},${y}
          h${crossWidth}
          v${crossStart}
          h${crossStart}
          v${crossWidth}
          h-${crossStart}
          v${crossStart}
          h-${crossWidth}
          v-${crossStart}
          h-${crossStart}
          v-${crossWidth}
          h${crossStart}z
        `;
      case 'flower':
        const cx = x + size / 2;
        const cy = y + size / 2;
        const radius = size / 2;
        const petalCount = 6;
        const innerRadius = radius * 0.4;
        let flowerPath = '';
        
        // Draw petals
        for (let i = 0; i < petalCount; i++) {
          const angle = (i * 2 * Math.PI) / petalCount;
          const nextAngle = ((i + 1) * 2 * Math.PI) / petalCount;
          const midAngle = (angle + nextAngle) / 2;
          
          const x1 = cx + radius * Math.cos(angle);
          const y1 = cy + radius * Math.sin(angle);
          const x2 = cx + radius * Math.cos(nextAngle);
          const y2 = cy + radius * Math.sin(nextAngle);
          const xMid = cx + radius * 1.3 * Math.cos(midAngle);
          const yMid = cy + radius * 1.3 * Math.sin(midAngle);
          
          flowerPath += `
            M${cx},${cy}
            Q${xMid},${yMid} ${x2},${y2}
            A${radius},${radius} 0 0 1 ${x1},${y1}
            Q${xMid},${yMid} ${cx},${cy}
          `;
        }
        
        // Add center circle
        flowerPath += `M${cx},${cy} m-${innerRadius},0 a${innerRadius},${innerRadius} 0 1,0 ${innerRadius*2},0 a${innerRadius},${innerRadius} 0 1,0 -${innerRadius*2},0`;
        
        return flowerPath;
      default:
        return `M${x},${y} h${size} v${size} h-${size}z`;
    }
  };

  const cellSize = 8;
  const totalSize = cellSize * 7;

  return (
    <MarkerExampleContainer>
      <svg width={totalSize} height={totalSize} viewBox={`0 0 ${totalSize} ${totalSize}`}>
        {pattern.map((row, rowIndex) => 
          row.map((cell, colIndex) => {
            if (cell === 1) {
              const x = colIndex * cellSize;
              const y = rowIndex * cellSize;
              return (
                <path
                  key={`${rowIndex}-${colIndex}`}
                  d={generateMarkerPath(x, y, cellSize)}
                  fill={markerColor}
                />
              );
            }
            return null;
          })
        )}
      </svg>
    </MarkerExampleContainer>
  );
};

const MarkerExampleContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background: white;
  border-radius: 8px;
  padding: 8px;

  svg {
    width: 100%;
    height: 100%;
  }
`; 